import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
    DOCUMENT
} from '@angular/core';
import { Coerce, KalDate } from '@kalidea/kaligraphi';
import { forkJoin, fromEvent, Observable, from, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Report, ReportPrintService } from 'src/app/_core/services/report-print.service';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import { ViewerDocumentService } from 'src/app/_shared/components/viewer-document/viewer-document.service';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist/legacy/build/pdf';
import { ProgressTrackerService } from 'src/app/_shared/components/progress/services/progress-tracker.service';
import { StepProgressTracker } from 'src/app/_shared/components/progress/models/progress-definitions';

GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.js';

export interface ViewerDocument {
    report: Report;
    removeCourrierId?: boolean;
    label?: string;
    fileName?: string;
}

@Component({
    selector: 'app-viewer-document',
    templateUrl: './viewer-document.component.html',
    styleUrls: ['./viewer-document.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ViewerDocumentComponent extends BaseComponent implements OnInit, OnDestroy {

    @Coerce('boolean')
    @Input()
    removeCourrierId = false;

    @Output() closeViewer = new EventEmitter<void>();

    @Input() filename: string;

    @Coerce('boolean')
    @Input()
    hideExportButton = false;

    @Coerce('boolean')
    @Input()
    isCloseButtonDisplayed = false;

    @Input() exportButtonLabel = 'Exporter';

    @ViewChild('canvas', {static: true}) canvas: ElementRef<HTMLCanvasElement>;

    pdfUrl: string;
    xlsUrl: string;

    private img: HTMLImageElement;
    private context: CanvasRenderingContext2D;
    private isDragging: boolean;
    private imgWidth: number;
    private imgHeight: number;
    private offsetX: number;
    private offsetY: number;
    private clickStartX: number;
    private clickStartY: number;
    private lastPositionX = 0;
    private lastPositionY = 0;
    private scaleLevel = 1.2;
    private _currentIndex = 0;
    private readonly progressTracker = inject(ProgressTrackerService);

    constructor(protected cdr: ChangeDetectorRef,
                private viewerDocumentService: ViewerDocumentService,
                private reportPrintService: ReportPrintService,
                @Inject(DOCUMENT) private readonly document: Document) {
        super(cdr);
    }

    @Input()
    set report(report: Report) {
        this.downloadReport(report);
    }

    private _src: string[];

    set src(src: string | string[]) {
        this._src = Array.isArray(src) ? src : [src];

        if (src) {
            this.loading = false;
            this.cdr.markForCheck();
            this.createImageCanva();
        } else {
            // au démarrage (!this._src)
            // on met à true si on a pas encore de src
            if (!this._src) {
                this.loading = true;
                this.cdr.markForCheck();
            }
        }
    }

    get isMultipage(): boolean {
        return this._src.length > 1;
    }

    get currentPage(): number {
        return this._currentIndex + 1;
    }

    get totalPages(): number {
        return this.isMultipage ? this._src.length : 1;
    }

    get hasPreviousPage() {
        return this._currentIndex > 0;
    }

    get hasNextPage() {
        return (this._currentIndex + 1 < this._src.length);
    }

    getFilename(extension: 'xls' | 'pdf') {
        return `${this.filename || 'sans-nom'}_${new KalDate().toFormat('yyyy-MM-dd')}.${extension}`
    }

    firstPage(): void {
        this.createImageCanva(0);
    }

    previousPage(): void {
        if (this.hasPreviousPage) {
            this.createImageCanva(this._currentIndex - 1);
        }
    }

    nextPage(): void {
        if (this.hasNextPage) {
            this.createImageCanva(this._currentIndex + 1);
        }
    }

    lastPage(): void {
        this.createImageCanva(this._src.length - 1);
    }

    handleMouseDown(e: MouseEvent): void {
        this.clickStartX = e.clientX - this.offsetX;
        this.clickStartY = e.clientY - this.offsetY;
        this.isDragging = true;
    }

    handleMouseLeave(e: MouseEvent): void {
        this.handleMouseUp(e);
    }

    handleMouseUp(e: MouseEvent): void {
        const moveX = parseInt(''+e.clientX, 10) - this.offsetX;
        const moveY = parseInt(''+e.clientY, 10) - this.offsetY;

        if (this.isDragging) {
            this.lastPositionX = moveX - this.clickStartX + this.lastPositionX;
            this.lastPositionY = moveY - this.clickStartY + this.lastPositionY;
        }

        this.isDragging = false;
    }

    handleMouseMove(e: MouseEvent): void {
        if (this.isDragging) {
            let moveX = parseInt(''+e.clientX, 10) - this.offsetX;
            let moveY = parseInt(''+e.clientY, 10) - this.offsetY;
            moveX = moveX - this.clickStartX + this.lastPositionX;
            moveY = moveY - this.clickStartY + this.lastPositionY;

            this.context.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
            this.context.drawImage(this.img, moveX, moveY, this.imgWidth, this.imgHeight);
        }
    }

    dezoom(): void {
        if (this.lastPositionX < 0) {
            this.lastPositionX = 0;
        }
        if (this.lastPositionY < 0) {
            this.lastPositionY = 0;
        }

        this.imgWidth = this.imgWidth / this.scaleLevel;
        this.imgHeight = this.imgHeight / this.scaleLevel;
        this.drawImage();
    }

    zoom(): void {
        this.imgWidth = this.imgWidth * this.scaleLevel;
        this.imgHeight = this.imgHeight * this.scaleLevel;
        this.drawImage();
    }

    drawImage(): void {
        this.context.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
        this.context.drawImage(this.img, this.lastPositionX, this.lastPositionY, this.imgWidth, this.imgHeight);
    }

    /**
     * Crée une Iframe et imprime le document affiché à l'intérieur.
     */
    printAction(): void {
        // Create the iframe
        const hiddenIframe = this.document.createElement('iframe') as HTMLIFrameElement;
        const removeIframe = () => {
            this.document.body.removeChild(hiddenIframe);
        };

        hiddenIframe.style.position = 'fixed';
        hiddenIframe.style.right = '0';
        hiddenIframe.style.bottom = '0';
        hiddenIframe.style.width = '0';
        hiddenIframe.style.height = '0';
        hiddenIframe.style.border = '0';
        hiddenIframe.src = this.pdfUrl;
        this.document.body.appendChild(hiddenIframe);
        hiddenIframe.contentWindow!.onbeforeunload = removeIframe;
        hiddenIframe.contentWindow!.onafterprint = removeIframe;

        hiddenIframe.onload = () => {
            hiddenIframe.contentWindow!.focus();
            hiddenIframe.contentWindow!.print();
        };
    }

    close(): void {
        this.closeViewer.emit();
    }

    /**
     * create an image contained in a canva
     */
    private createImageCanva(index: number = 0): void {
        this.img = new Image();
        this.img.src = this._src[index];
        this._currentIndex = index;

        this.context = this.canvas.nativeElement.getContext('2d');
        this.offsetX = this.canvas.nativeElement.offsetLeft;
        this.offsetY = this.canvas.nativeElement.offsetTop;

        this.watch$ = fromEvent(this.img, 'load').pipe(
            tap(() => {
                this.canvas.nativeElement.width = this.img.naturalWidth;
                this.canvas.nativeElement.height = this.img.height;
                this.imgWidth = this.img.naturalWidth;
                this.imgHeight = this.img.naturalHeight;

                this.drawImage();
            }),
            wheneverEnd(() => this.viewerDocumentService.loadingDocument = false)
        );
    }

    private downloadReport(report: Report): void {
        this.viewerDocumentService.loadingDocument = true;
        this.src = null;
        this.loading = true;

        // Utiliser le service de progression avec 3 étapes
        this.watch$ = this.progressTracker.execute<string[]>(report.code, [
            // Étape 1: Récupération des données (PDF + XLS)
            () => this.fetchReportData(report),

            // Étape 2: Traitement des résultats
            (_, results: string[][]) => this.processReportResults(results),

            // Étape 3: Extraction des images (avec progress réel page par page)
            (tracker) => this.extractImagesWithProgress(tracker)

        ]).pipe(
            tap((images: string[]) => {
                this.src = images;
                this.loading = false;
            }),
            catchError(() => {
                this.src = null;
                this.loading = false;
                return of([]);
            }),
            wheneverEnd(() => this.viewerDocumentService.loadingDocument = false)
        );
    }

    /**
     * Étape 1: Récupération des données PDF et XLS
     */
    private fetchReportData(report: Report): Observable<string[][]> {
        const printOperations: Observable<string[]>[] = [
            this.reportPrintService.printPdf([report])
        ];

        if (!this.hideExportButton) {
            printOperations.push(this.reportPrintService.printXLS([report]));
        }

        return forkJoin(printOperations);
    }

    /**
     * Étape 2: Traitement des résultats (extraction URLs)
     */
    private processReportResults(results: string[][]): Observable<string> {
        const [pdf, xls] = results;

        if (arrayNotEmpty(pdf)) {
            this.pdfUrl = pdf[0];
        }

        if (xls && arrayNotEmpty(xls)) {
            this.xlsUrl = xls[0];
        }

        return of(this.pdfUrl);
    }

    /**
     * Étape 3: Extraction des images avec progress réel
     */
    private extractImagesWithProgress(tracker: StepProgressTracker | null): Observable<string[]> {
        return from(this.doExtractImages(tracker));
    }

    /**
     * Extrait les images depuis un PDF en utilisant pdf.js
     * Reporte le progress réel page par page
     */
    private async doExtractImages(tracker: StepProgressTracker | null): Promise<string[]> {
        const images: string[] = [];
        const loadingTask = getDocument(this.pdfUrl);
        const pdf = await loadingTask.promise;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const viewport = page.getViewport({ scale: 2.0 });

            const canvas = this.document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            await page.render({ canvasContext: context, viewport }).promise;

            images.push(canvas.toDataURL('image/png'));

            // Reporter le progress réel
            tracker?.setProgress(
                (pageNum / pdf.numPages) * 100,
                `Page ${pageNum}/${pdf.numPages}`
            );
        }

        return images;
    }

    ngOnInit(): void {
        this.loading = true;
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.viewerDocumentService.loadingDocument = false;
    }

}
